import React, { useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MainLayout from "../../../../layouts/MainLayout";
import PageHeader from "../../../../components/PageHeader";
import "../../../../assets/styles/custom-styles.css";
import { removeMask } from "../../../../utils/maskUtils";
import useLoader from "../../../../hooks/useLoader";
import useNotification from "../../../../hooks/useNotification";
import useForm from "../../../../hooks/useForm";
import { addressFields } from "../../../../constants/forms/addressFields";
import Form from "../../../../components/forms/Form";
import {
  cleanPayload,
  normalizeFormErrors,
  setDefaultFieldValues,
} from "../../../../utils/objectUtils";
import useBaseService from "../../../../hooks/services/useBaseService";
import { entities } from "../../../../constants/entities";
import SectionHeader from "../../../../components/forms/SectionHeader";
import SimpleBody from "../../../../components/forms/SimpleBody";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const CreateCustomerAddressPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const { formData, setFormData, handleChange } = useForm(
    setDefaultFieldValues(addressFields),
  );
  const {
    post: baseServicePost,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);
  const queryClient = useQueryClient();

  const fetchCepData = useCallback(
    async (value) => {
      if (value && removeMask(value).length === 8) {
        showLoader();
        try {
          const response = await fetch(
            `https://viacep.com.br/ws/${removeMask(value)}/json/`,
          );
          if (!response.ok) throw new Error("Erro ao buscar o CEP");

          const data = await response.json();
          if (data.erro) throw new Error("CEP não encontrado");

          setFormData((prev) => ({
            address: {
              ...prev.address,
              street: data.logradouro || "",
              district: data.bairro || "",
              city: data.localidade || "",
              state: data.estado || "",
              country: "Brasil",
            },
          }));
          showNotification("success", "Endereço preenchido automaticamente!");
        } catch (error) {
          showNotification("error", error.message);
        } finally {
          hideLoader();
        }
      }
    },
    [hideLoader, setFormData, showLoader, showNotification],
  );

  useEffect(() => {
    if (formData.address.zip && removeMask(formData.address.zip).length === 8) {
      fetchCepData(formData.address.zip);
    }
  }, [formData.address.zip]);

  const createAddressMutation = useMutation({
    mutationFn: async (data) => {
      const response = await baseServicePost(
        entities.customers.addresses.create(id),
        data,
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 201) {
        navigate(`/aluguel/clientes/detalhes/${id}`);
        queryClient.invalidateQueries({ queryKey: ["customerAddresses"] });
      }
    },
    onError: (err) => {
      console.error("onError ", err);
      if (err.response?.data?.errors) {
        setFormErrors(err.response.data.errors);
      }
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
    },
  });

  const handleSubmit = async () => {
    try {
      const cleanedData = cleanPayload(formData);
      const sanitizedData = {
        address: {
          ...cleanedData.address,
          addressable_type: "customer",
          number: cleanedData.address.number
            ? String(cleanedData.address.number)
            : null,
          zip: cleanedData.address.zip
            ? removeMask(cleanedData.address.zip)
            : "",
        },
      };

      return await createAddressMutation.mutateAsync(sanitizedData);
    } catch (error) {
      console.error("Erro ao cadastrar endereço:", {
        status: error.status,
        message: error.message,
      });
    }
  };

  const handleBack = () => {
    navigate(`/aluguel/clientes/detalhes/${id}`);
  };

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Cadastro de Endereço do Cliente"
        showBackButton={true}
        backUrl={`/aluguel/clientes/detalhes/${id}`}
      />
      <div className="container-fluid p-1">
        <Form
          initialFormData={formData}
          handleBack={handleBack}
          textSubmit="Cadastrar"
          textLoadingSubmit="Cadastrando..."
          onSubmit={handleSubmit}>
          {() => {
            const renderedSections = new Set();

            return addressFields.map((section, index) => {
              const sectionType = section.section.split(" - ")[0];
              const showHeader = !renderedSections.has(sectionType);

              if (showHeader) {
                renderedSections.add(sectionType);
              }

              return (
                <div key={section.section + " " + index}>
                  {showHeader && (
                    <SectionHeader
                      section={section.section}
                      viewTable={false}
                      setViewTable={() => {}}
                      addFieldsInData={() => {}}
                    />
                  )}

                  <SimpleBody
                    fields={section.fields}
                    formErrors={normalizeFormErrors(formErrors)}
                    formData={formData}
                    handleFieldChange={handleChange}
                    getOptions={() => {}}
                    getSelectedValue={() => {}}
                  />
                </div>
              );
            });
          }}
        </Form>
      </div>
    </MainLayout>
  );
};

export default CreateCustomerAddressPage;
